import {ActionMenuSection} from '#/components/actionmenu/types';
import {SLQueryParamConfig} from '#/contexts/query-params-provider';
import {useTypedNavigate} from '#/hooks/use-typed-navigate';
import {InformationTab, PortfolioTab, RentTab, TradeTab} from '#/navigation/tabs';
import {ExchangeRoute} from '#/routes/exchange/exchange-route';
import {landHasMarket} from '#/utils/land-utils';
import {Land, UnlistedLand} from '@shareland/shareland-common';
import {useMemo} from 'react';
import {useLocation} from 'react-router-dom';
import {useQueryParams} from 'use-query-params';

interface QueryNavigationActions {
  openLandOnMap: (
    land?: Land | UnlistedLand,
    action?: ActionMenuSection.INFORMATION | ActionMenuSection.TRADE | ActionMenuSection.RENT,
  ) => void;
  openPortfolioTab: (tab: PortfolioTab) => void;
  actionMenu: {
    close: () => void;
    openInformation: (land?: Land | UnlistedLand, tab?: InformationTab) => void;
    openTrade: (land?: Land, tab?: TradeTab, actionSource?: ActionMenuSection.INFORMATION) => void;
    openRent: (land?: Land, tab?: RentTab, actionSource?: ActionMenuSection.INFORMATION) => void;
    openTransaction: () => void;
  };
}

export const useQueryNavigationActions = (): QueryNavigationActions => {
  // External hooks
  const [query, setQuery] = useQueryParams<SLQueryParamConfig>();
  const location = useLocation();
  const navigate = useTypedNavigate();

  return useMemo(
    () => ({
      openLandOnMap: (land, action) => {
        if (!land || landHasMarket(land)) {
          navigate(ExchangeRoute.MAP, {
            action,
            landToken: land ? (land as Land).tokenSymbol : query.landToken,
            informationTabId: InformationTab.INFORMATION,
          });
        } else {
          navigate(ExchangeRoute.MAP, {landToken: land.tokenSymbol, action: ActionMenuSection.INFORMATION});
        }
      },
      openPortfolioTab: (tab = PortfolioTab.ACTIVITY) => {
        if (location.pathname.includes(ExchangeRoute.PORTFOLIO)) {
          setQuery({portfolioTabId: tab}, 'replaceIn');
        } else {
          const queryParams = {
            portfolioTabId: tab,
          };
          navigate(ExchangeRoute.PORTFOLIO, queryParams);
        }
      },
      actionMenu: {
        close: () => {
          setQuery(
            {
              landToken: undefined,
              action: undefined,
              actionSource: undefined,
              transaction: undefined,
              informationTabId: undefined,
              tradeTabId: undefined,
              rentTabId: undefined,
            },
            'pushIn',
          );
        },
        openInformation: (land, tab) => {
          setQuery(
            {
              landToken: land ? (land as Land).tokenSymbol : query.landToken,
              action: ActionMenuSection.INFORMATION,
              actionSource: undefined,
              transaction: undefined,
              informationTabId: tab ?? query.informationTabId ?? InformationTab.INFORMATION,
            },
            'pushIn',
          );
        },
        openTrade: (land, tab, actionSource) => {
          setQuery(
            {
              landToken: land?.tokenSymbol ?? query.landToken,
              action: ActionMenuSection.TRADE,
              actionSource: actionSource ?? query.actionSource,
              transaction: undefined,
              tradeTabId: tab ?? query.tradeTabId ?? TradeTab.BUY,
            },
            'pushIn',
          );
        },
        openRent: (land, tab, actionSource) => {
          setQuery(
            {
              landToken: land?.tokenSymbol ?? query.landToken,
              action: ActionMenuSection.RENT,
              actionSource: actionSource ?? query.actionSource,
              transaction: undefined,
              rentTabId: tab ?? query.rentTabId ?? RentTab.RENT,
            },
            'pushIn',
          );
        },
        openTransaction: () => {
          setQuery({action: ActionMenuSection.TRANSACTION}, 'pushIn');
        },
      },
    }),
    [location.pathname, query, setQuery, navigate],
  );
};
